import { useMemo } from "react";
import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Container from "@amzn/awsui-components-react/polaris/container";

import Grid from "@amzn/awsui-components-react/polaris/grid";
import Header from "@amzn/awsui-components-react/polaris/header";
import Link from "@amzn/awsui-components-react/polaris/link";

import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";

import {
  ACCESS_REQUEST_URL,
  ACCESS_REQUEST_URL_CANADA,
  ROUTES,
  WC_USER_GUIDE_URL,
  CARGO_USER_GUIDE_URL,
  IPL_USER_GUIDE_URL,
} from "../../app/constants";
import { useAppSelector } from "../../redux/hooks";
import { getUserClaimCategories } from "../../auth/claimCategoryUtils";
import { LineOfBusiness } from "../../app/types";

const renderPageHeader = () => (
  <div className="custom-home__header">
    <Box padding={{ vertical: "xxxl", horizontal: "s" }}>
      <div className="custom-home__grid-container">
        <Grid
          disableGutters
          gridDefinition={[
            { offset: { l: 2, xxs: 1 }, colspan: { l: 8, xxs: 10 } },
            {
              colspan: { xl: 6, l: 5, s: 6, xxs: 10 },
              offset: { l: 2, xxs: 1 },
            },
            {
              colspan: { xl: 2, l: 3, s: 4, xxs: 10 },
              offset: { s: 0, xxs: 1 },
            },
          ]}
        >
          <Box fontWeight="light" padding={{ top: "xs" }}>
            <div className="custom-home__category">Amazon FinTech</div>
          </Box>
          <div className="custom-home__header-title">
            <Box
              variant="h1"
              fontWeight="heavy"
              padding="n"
              fontSize="display-l"
              color="inherit"
            >
              Nucleus Claim Intake
            </Box>
            <Box
              fontWeight="light"
              padding={{ bottom: "s" }}
              fontSize="display-l"
              color="inherit"
            >
              Easy and reliable claim submission
            </Box>
            <Box variant="p" fontWeight="light">
              <span className="custom-home__header-sub-title">
                Nucleus, a new tool for initiating Workers&apos; Compensation
                (WC), Cargo and International Product Liability(IPL) claims, is
                now live in Air, Customer Fulfillment, and Sort Center (ATS)
                business lines. Nucleus offers a simple, intelligent, and
                consistent experience for submitting claims with enhanced data
                quality and an easy-to-use interface that reduces the time to
                submit a referral. The Nucleus tool is designed for Injury
                Prevention Specialist, Onsite Medical Representatives, Workplace
                Health & Safety Specialist, Workplace Health & Safety Managers,
                Cargo specialists, and Legal/Risk Management teams who submit
                claims as part of their role.
              </span>
            </Box>
          </div>
          <div className="custom-home__header-cta">
            <Container>
              <SpaceBetween size="xl">
                <Box variant="p">
                  Request access to claim submission forms. Access for these
                  forms is intended for WHS teams or Cargo specialist teams,
                  please click on the button below to file a request access form
                  if required
                </Box>
                <SpaceBetween direction="horizontal" size="s">
                  <Button
                    href={ACCESS_REQUEST_URL}
                    variant="primary"
                    external={true}
                  >
                    Request Access (US)
                  </Button>
                  <Button
                    href={ACCESS_REQUEST_URL_CANADA}
                    variant="normal"
                    external={true}
                  >
                    Request Access (Canada)
                  </Button>
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          </div>
        </Grid>
      </div>
    </Box>
  </div>
);

const renderClaimIntakeForms = (categories: Set<LineOfBusiness>) => (
  <>
    {categories.has(LineOfBusiness.WORKERS_COMPENSATION) && (
      <div>
        <Box variant="h3" padding={{ top: "n" }}>
          Create Workers&apos; Compensation claim
        </Box>
        <Box variant="p">
          Create a WC claim for an employee that has reported an injury that
          happened in one of the supported sites. this service is intended for
          Injury Prevention Specialists, Onsite Medical Representatives,
          Workplace Health & Safety Specialists
        </Box>
        <Link
          ariaLabel="Click to go to the WC claim intake form"
          variant="primary"
          href={ROUTES.WC_CLAIM_INTAKE_FORM}
        >
          Go to WC Claim Intake form
        </Link>
      </div>
    )}
    {categories.has(LineOfBusiness.CARGO) && (
      <div>
        <Box variant="h3" padding={{ top: "n" }}>
          Create Cargo claim
        </Box>
        <Box variant="p">
          Create a Cargo claim for a team that reported loss or damage for cargo
          over the value of $1,000,000 USD. This service is intended for Cargo
          Claims Team & Cargo business units (i.e. Prime Air) .
        </Box>
        <Link
          ariaLabel="Click to go to the Cargo claim intake form"
          variant="primary"
          href={ROUTES.CARGO_CLAIM_INTAKE_FORM}
        >
          Go to Cargo Claim Intake Form
        </Link>
      </div>
    )}
    {categories.has(LineOfBusiness.IPL) && (
      <div>
        <Box variant="h3" padding={{ top: "n" }}>
          Create IPL claim
        </Box>
        <Box variant="p">
          Create an International Product Liability claim for incidents
          involving Amazon products in supported countries. This service is
          intended for Legal and Risk Management teams.
        </Box>
        <Link
          ariaLabel="Click to go to the IPL claim intake form"
          variant="primary"
          href={ROUTES.IPL_CLAIM_INTAKE_FORM}
        >
          Go to IPL Claim Intake Form
        </Link>
      </div>
    )}
  </>
);

const renderClaimsSearch = (categories: Set<LineOfBusiness>) => {
  if (categories.size === 0) return null;
  return (
    <div>
      <Box variant="h3" padding={{ top: "n" }}>
        Search for Claims
      </Box>
      <Box variant="p">Search for claims submitted in Nucleus</Box>
      <SpaceBetween direction="vertical" size="xs">
        {categories.has(LineOfBusiness.WORKERS_COMPENSATION) && (
          <Link
            external={true}
            ariaLabel="Click to go to the WC claims search page, opens in new tab"
            variant="primary"
            href={ROUTES.CLAIMS_HISTORY}
          >
            WC claims search
          </Link>
        )}
        {categories.has(LineOfBusiness.CARGO) && (
          <Link
            external={true}
            ariaLabel="Click to go to the Cargo claims search page, opens in new tab"
            variant="primary"
            href={ROUTES.CARGO_CLAIMS_HISTORY}
          >
            Cargo claims search
          </Link>
        )}
        {categories.has(LineOfBusiness.IPL) && (
          <Link
            external={true}
            ariaLabel="Click to go to the IPL claims search page, opens in new tab"
            variant="primary"
            href={ROUTES.IPL_CLAIMS_HISTORY}
          >
            IPL claims search
          </Link>
        )}
      </SpaceBetween>
    </div>
  );
};

const renderDocumentsHub = (categories: Set<LineOfBusiness>) => {
  if (!categories.has(LineOfBusiness.WORKERS_COMPENSATION)) return null;
  return (
    <div>
      <Box variant="h3" padding={{ top: "n" }}>
        Documents Hub
      </Box>
      <Box variant="p">
        Navigate through the documents hub where you will be able to find all
        necessary documents for Workers&apos; Compensation claim submission
        including state forms, province forms, medical panel data, and medical
        authorization documents
      </Box>
      <Link
        external={true}
        ariaLabel="Click to go to the documents hub page, opens in new tab"
        variant="primary"
        href={ROUTES.WORKERS_COMPENSATION_DOCUMENTS}
      >
        Go to Documents Hub
      </Link>
    </div>
  );
};

const renderRelatedServices = () => (
  <div>
    <Box variant="h1" tagOverride="h2" padding={{ bottom: "s", top: "n" }}>
      Related Services
    </Box>
    <Container>
      <ColumnLayout columns={2} variant="text-grid">
        <div>
          <Box variant="h3" padding={{ top: "n" }}>
            <Link
              fontSize="heading-m"
              variant="primary"
              href="https://w.amazon.com/bin/view/Main/Risk_Management/GRMC_Tech/Origami"
              external={true}
            >
              Origami Risk
            </Link>
          </Box>
          <Box variant="p">
            The Origami project is a cross-functional effort led by the Treasury
            Technology team in partnership with the Global Risk Management &
            Claims (GRMC) org and FinTech Treasury teams working hard to ensure
            a value-added launch for our customers
          </Box>
        </div>
      </ColumnLayout>
    </Container>
  </div>
);

const renderGettingStarted = (categories: Set<LineOfBusiness>) => (
  <Container header={<Header variant="h2">Getting Started</Header>}>
    <SpaceBetween direction="vertical" size="s">
      {categories.has(LineOfBusiness.WORKERS_COMPENSATION) && (
        <Link
          href={WC_USER_GUIDE_URL}
          external={true}
          ariaLabel="Click to go to the WC user guide, opens in new tab"
        >
          WC User&apos;s Guide
        </Link>
      )}
      {categories.has(LineOfBusiness.CARGO) && (
        <Link
          href={CARGO_USER_GUIDE_URL}
          external={true}
          ariaLabel="Click to go to the Cargo user guide, opens in new tab"
        >
          Cargo User&apos;s Guide
        </Link>
      )}
      {categories.has(LineOfBusiness.IPL) && (
        <Link
          href={IPL_USER_GUIDE_URL}
          external={true}
          ariaLabel="Click to go to the IPL user guide, opens in new tab"
        >
          IPL User&apos;s Guide
        </Link>
      )}
    </SpaceBetween>
  </Container>
);

export const LandingPageContent = () => {
  const userRoles = useAppSelector((state) => state.auth.user.roles);
  const categories = useMemo(
    () => getUserClaimCategories(userRoles),
    [userRoles]
  );

  return (
    <Box margin={{ bottom: "l" }}>
      {renderPageHeader()}
      <Box padding={{ top: "xxxl", horizontal: "s" }}>
        <div className="custom-home__grid-container">
          <Grid
            disableGutters
            gridDefinition={[
              {
                colspan: { xl: 6, l: 5, s: 6, xxs: 10 },
                offset: { l: 2, xxs: 1 },
              },
              {
                colspan: { xl: 2, l: 3, s: 4, xxs: 10 },
                offset: { s: 0, xxs: 1 },
              },
            ]}
          >
            <div className="custom-home__main-content">
              <SpaceBetween size="xxl">
                <div>
                  <Box
                    variant="h1"
                    tagOverride="h2"
                    padding={{ bottom: "s", top: "n" }}
                  >
                    What can I do?
                  </Box>
                  <Container>
                    <ColumnLayout columns={2} variant="text-grid">
                      {renderClaimIntakeForms(categories)}
                      {renderClaimsSearch(categories)}
                      {renderDocumentsHub(categories)}
                    </ColumnLayout>
                  </Container>
                </div>
                {renderRelatedServices()}
              </SpaceBetween>
            </div>
            <div className="custom-home__sidebar">
              <SpaceBetween size="xxl">
                {renderGettingStarted(categories)}
              </SpaceBetween>
            </div>
          </Grid>
        </div>
      </Box>
    </Box>
  );
};
